import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { Panel } from 'primereact/panel';
import { Button } from 'primereact/button';
import './additionalRespondentInformation.css';
import { useQuery } from '@apollo/react-hooks';
import _ from 'lodash';
import { injectIntl } from 'react-intl';
import ManageLocalARIFields from './LocalManage/ManageLocalARIFields';
import { withAccount } from '../../../components/AccountContext';
import { ACCOUNT_QUERY, USER_AGREEMENT } from './queries';
import { Loading } from '../../../components/Loading';
import CreateGlobalARIFields from './GlobalCreate/CreateGlobalARIFields';
import ManageGlobalARIFields from './GlobalManage/ManageGlobalARIFields';
import CreateLocalARIFields from './LocalCreate/CreateLocalARIFields';
import ManageAllowLocalFieldsUse from './ManageAllowLocalFieldsUse';
import SubmitDeclarationModal from './SubmitDeclarationModal';
import messages from './intl-messages';


const AdditionalRespondentInformation = ({ currentAccount, intl: { formatMessage } }) => {
  const [isMotherAccount, setIsMotherAccount] = useState(false);
  const [isSubAccount, setIsSubAccount] = useState(false);
  const [creatingGlobalARIFields, setCreatingGlobalARIFields] = useState(false);
  const [managingGlobalARIFields, setManagingGlobalARIFields] = useState(false);
  const [creatingLocalARIFields, setCreatingLocalARIFields] = useState(false);
  const [managingLocalARIFields, setManagingLocalARIFields] = useState(false);
  const [accountData, setAccountData] = useState(null);
  const [canUseLocalFields, setCanUseLocalFields] = useState(false);
  const [agreed, setAgreed] = useState(null);

  const [creatingARIFields, setCreatingARIFields] = useState(false);
  const [managingARIFields, setManagingARIFields] = useState(false);

  const { data, refetch: refetchData } = useQuery(ACCOUNT_QUERY, { variables: { accountId: currentAccount } });
  const { data: agreement, refetch: refetchAgreement } = useQuery(USER_AGREEMENT, { variables: { first: 999 } });

  const maxGlobalFields = 5;
  const maxLocalFields = 10;

  const ariTypeOptions = [
    { label: formatMessage(messages.numberLabel), value: 'NUMBER', key: 'number' },
    { label: formatMessage(messages.textLabel), value: 'TEXT', key: 'text' },
    { label: formatMessage(messages.dropdownLabel), value: 'DROPDOWN', key: 'dropdown' },
  ];

  const ariPanelHeader = formatMessage(messages.ariPanelHeader);

  useEffect(() => {
    refetchData();
    if (data && data.account) {
      setIsMotherAccount(data.account.canCreateGlobalAriFields);
      setIsSubAccount(!data.account.canCreateGlobalAriFields);
      setCanUseLocalFields(data.account.canUseLocalAriFields);
      setAccountData(data);
    }
  }, [data, refetchData]);

  useEffect(() => {
    refetchAgreement();
    if (agreement && agreement.viewer) {
      const accountEdge = _.find(agreement.viewer.accounts.edges,
        edge => edge.node.id === currentAccount);
      if (accountEdge) {
        setAgreed(accountEdge.agreedToGdprTerms);
      }
    }
  }, [agreement, refetchAgreement, currentAccount]);

  return (!accountData || agreed === null) ? <Loading /> : (
    <div>
      <Panel header={(
        <div className="p-grid p-nogutter">
          <div className="p-col">
            {ariPanelHeader}
          </div>
        </div>
      )}
      >
        {
          isMotherAccount && (
            <div className="p-grid p-justify-center">
              {
                agreed && (
                <div className="p-col-12" style={{ textAlign: 'center' }}>
                  <ManageAllowLocalFieldsUse
                    data={accountData}
                    currentAccount={currentAccount}
                  />
                </div>
                )
              }

              <div className="p-col-3">
                <Button
                  type="button"
                  style={{ height: '96px' }}
                  className={creatingGlobalARIFields ? 'p-button-info' : 'p-button-secondary'}
                  label={formatMessage(messages.createGlobalButtonLabel)}
                  onClick={() => {
                    setCreatingGlobalARIFields(!creatingGlobalARIFields);
                    setManagingGlobalARIFields(false);
                    setCreatingLocalARIFields(false);
                    setManagingLocalARIFields(false);
                  }}
                />
              </div>
              <div className="p-col-3">
                <Button
                  type="button"
                  style={{ height: '96px' }}
                  className={managingGlobalARIFields ? 'p-button-info' : 'p-button-secondary'}
                  label={formatMessage(messages.manageGlobalButtonLabel)}
                  onClick={() => {
                    setCreatingGlobalARIFields(false);
                    setManagingGlobalARIFields(!managingGlobalARIFields);
                    setCreatingLocalARIFields(false);
                    setManagingLocalARIFields(false);
                  }}
                />
              </div>
              <div className="p-col-3">
                <Button
                  type="button"
                  style={{ height: '96px' }}
                  className={creatingLocalARIFields ? 'p-button-info' : 'p-button-secondary'}
                  label={formatMessage(messages.createLocalButtonLabel)}
                  onClick={() => {
                    setCreatingGlobalARIFields(false);
                    setManagingGlobalARIFields(false);
                    setCreatingLocalARIFields(!creatingLocalARIFields);
                    setManagingLocalARIFields(false);
                  }}
                />
              </div>
              <div className="p-col-3">
                <Button
                  type="button"
                  style={{ height: '96px' }}
                  className={managingLocalARIFields ? 'p-button-info' : 'p-button-secondary'}
                  label={formatMessage(messages.manageLocalButtonLabel)}
                  onClick={() => {
                    setCreatingGlobalARIFields(false);
                    setManagingGlobalARIFields(false);
                    setCreatingLocalARIFields(false);
                    setManagingLocalARIFields(!managingLocalARIFields);
                  }}
                />
              </div>

              <br />

              {creatingGlobalARIFields && (
                <>
                  {
                    agreed && (
                      <CreateGlobalARIFields
                        maxFieldCount={maxGlobalFields}
                        currentFieldCount={data.account.globalAriFields.length}
                        ariTypeOptions={ariTypeOptions}
                        data={data}
                      />
                    )
                  }
                  {
                    !agreed && (
                      <SubmitDeclarationModal
                        isVisible={!agreed}
                        currentAccount={currentAccount}
                        onCancel={() => setCreatingGlobalARIFields(false)}
                      />
                    )
                  }
                </>
              )
              }

              {managingGlobalARIFields && (
                <>
                  {
                    agreed && (
                      <ManageGlobalARIFields
                        data={data}
                      />
                    )
                  }
                  {
                    !agreed && (
                      <SubmitDeclarationModal
                        isVisible={!agreed}
                        currentAccount={currentAccount}
                        onCancel={() => setManagingGlobalARIFields(false)}
                      />
                    )
                  }
                </>
              )}

              {creatingLocalARIFields && (
                <>
                  {
                    agreed && (
                      <CreateLocalARIFields
                        maxFieldCount={maxLocalFields}
                        currentFieldCount={data.account.localAriFields.length}
                        ariTypeOptions={ariTypeOptions}
                        data={data}
                      />
                    )
                  }
                  {
                    !agreed && (
                      <SubmitDeclarationModal
                        isVisible={!agreed}
                        currentAccount={currentAccount}
                        onCancel={() => setCreatingLocalARIFields(false)}
                      />
                    )
                  }
                </>
              )}

              {managingLocalARIFields && (
                <>
                  {
                    agreed && (
                      <ManageLocalARIFields
                        data={data}
                      />
                    )
                  }
                  {
                    !agreed && (
                      <SubmitDeclarationModal
                        isVisible={!agreed}
                        currentAccount={currentAccount}
                        onCancel={() => setManagingLocalARIFields(false)}
                      />
                    )
                  }
                </>
              )}

            </div>
          )}

        {
          isSubAccount && (canUseLocalFields
            ? (
              <div className="p-grid p-justify-center">
                <div className="p-col-6">
                  <Button
                    type="button"
                    className={creatingARIFields ? 'p-button-info' : 'p-button-secondary'}
                    label={formatMessage(messages.subaccountCreateButtonLabel)}
                    style={{ width: '100%', height: '96px' }}
                    onClick={() => {
                      setCreatingARIFields(!creatingARIFields);
                      setManagingARIFields(false);
                    }}
                  />
                </div>
                <div className="p-col-6">
                  <Button
                    type="button"
                    className={managingARIFields ? 'p-button-info' : 'p-button-secondary'}
                    label={formatMessage(messages.subaccountManageButtonLabel)}
                    style={{ width: '100%', height: '96px' }}
                    onClick={() => {
                      setCreatingARIFields(false);
                      setManagingARIFields(!managingARIFields);
                    }}
                  />
                </div>

                <br />

                {creatingARIFields && (
                  <>
                    {
                      agreed
                      && (
                        <CreateLocalARIFields
                          maxFieldCount={maxLocalFields}
                          currentFieldCount={data.account.localAriFields.length}
                          ariTypeOptions={ariTypeOptions}
                          data={data}
                        />
                      )
                    }
                    {
                      !agreed && (
                        <SubmitDeclarationModal
                          isVisible={!agreed}
                          currentAccount={currentAccount}
                          onCancel={() => setCreatingARIFields(false)}
                        />
                      )
                    }
                  </>
                )}

                {managingARIFields && (
                  <>
                    {
                      agreed && (
                        <ManageLocalARIFields
                          data={data}
                        />
                      )
                    }
                    {
                      !agreed && (
                        <SubmitDeclarationModal
                          isVisible={!agreed}
                          currentAccount={currentAccount}
                          onCancel={() => setManagingARIFields(false)}
                        />
                      )
                    }
                  </>
                )}
              </div>
            ) : (
              <div>
                <small>
                  {formatMessage(messages.ariFieldsNotEnabledMessage)}
                </small>
              </div>
            ))
        }
      </Panel>
    </div>
  );
};

AdditionalRespondentInformation.propTypes = {
  currentAccount: PropTypes.string.isRequired,
  intl: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default withAccount(injectIntl(AdditionalRespondentInformation));
